import React, { useCallback, useContext, useMemo, useRef, useState } from "react";
import Container from "@material-ui/core/Container";
import Grid from "@material-ui/core/Grid";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import GetAppIcon from "@material-ui/icons/GetApp";
import RefreshIcon from "@material-ui/icons/Refresh";
import AssessmentOutlinedIcon from "@material-ui/icons/AssessmentOutlined";
import PeopleOutlineIcon from "@material-ui/icons/PeopleOutline";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import AssignmentOutlinedIcon from "@material-ui/icons/AssignmentOutlined";
import ErrorOutlineOutlinedIcon from "@material-ui/icons/ErrorOutlineOutlined";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip as RechartsTooltip,
  Legend,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid
} from "recharts";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const chartPalette = [
  "#0f766e",
  "#14b8a6",
  "#22c55e",
  "#f59e0b",
  "#f97316",
  "#3b82f6",
  "#8b5cf6",
  "#ef4444"
];

const periodOptions = [
  { value: "today", label: "Hoje" },
  { value: "7d", label: "7 dias" },
  { value: "30d", label: "30 dias" },
  { value: "month", label: "Mês atual" }
];

const dateBasisOptions = [
  { value: "closedAt", label: "Data de fechamento" },
  { value: "createdAt", label: "Data de abertura" }
];

const useStyles = makeStyles(theme => {
  const isDark = theme.palette.type === "dark";

  return ({
  container: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4)
  },
  heroCard: {
    padding: theme.spacing(3),
    borderRadius: 28,
    background: isDark
      ? "linear-gradient(135deg, rgba(15, 23, 42, 0.96) 0%, rgba(17, 24, 39, 0.94) 52%, rgba(6, 78, 59, 0.16) 100%)"
      : "linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(240,248,246,0.98) 52%, rgba(232,244,241,0.98) 100%)",
    border: isDark ? "1px solid rgba(45, 212, 191, 0.16)" : "1px solid rgba(18, 140, 126, 0.12)",
    boxShadow: isDark ? "0 22px 56px rgba(2, 6, 23, 0.38)" : "0 22px 56px rgba(18, 140, 126, 0.12)",
    marginBottom: theme.spacing(3)
  },
  heroHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column"
    }
  },
  heroIdentity: {
    minWidth: 0
  },
  heroNoticeSlot: {
    flex: 1,
    minWidth: 260,
    maxWidth: 420,
    [theme.breakpoints.down("md")]: {
      maxWidth: "100%"
    },
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      minWidth: 0
    }
  },
  heroEyebrow: {
    color: isDark ? "#5eead4" : "#0b6b60",
    fontWeight: 800,
    letterSpacing: "0.08em",
    fontSize: "0.75rem",
    textTransform: "uppercase"
  },
  heroTitle: {
    marginTop: theme.spacing(1),
    fontWeight: 800,
    lineHeight: 1.08
  },
  heroActions: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap"
  },
  heroActionsPanel: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    minWidth: 0,
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      justifyContent: "flex-start"
    }
  },
  heroActionsLabel: {
    display: "none"
  },
  commercialAlert: {
    padding: theme.spacing(1.5, 2),
    borderRadius: 20,
    border: isDark ? "1px solid rgba(245, 158, 11, 0.32)" : "1px solid rgba(245, 158, 11, 0.24)",
    background: isDark
      ? "linear-gradient(135deg, rgba(120, 53, 15, 0.44) 0%, rgba(146, 64, 14, 0.28) 100%)"
      : "linear-gradient(135deg, rgba(255, 251, 235, 0.98) 0%, rgba(254, 243, 199, 0.98) 100%)",
    boxShadow: isDark ? "0 10px 24px rgba(15, 23, 42, 0.30)" : "0 10px 24px rgba(245, 158, 11, 0.10)"
  },
  commercialAlertDanger: {
    border: isDark ? "1px solid rgba(248, 113, 113, 0.34)" : "1px solid rgba(239, 68, 68, 0.22)",
    background: isDark
      ? "linear-gradient(135deg, rgba(127, 29, 29, 0.44) 0%, rgba(153, 27, 27, 0.28) 100%)"
      : "linear-gradient(135deg, rgba(254, 242, 242, 0.98) 0%, rgba(254, 226, 226, 0.98) 100%)"
  },
  commercialAlertTitle: {
    fontWeight: 800,
    color: isDark ? "#fde68a" : "#92400e"
  },
  commercialAlertTitleDanger: {
    color: isDark ? "#fecaca" : "#991b1b"
  },
  commercialAlertText: {
    marginTop: theme.spacing(0.5),
    color: isDark ? "#fcd34d" : "#7c2d12",
    lineHeight: 1.5
  },
  commercialAlertTextDanger: {
    color: isDark ? "#fda4af" : "#7f1d1d"
  },
  actionButton: {
    borderRadius: 999,
    fontWeight: 700,
    paddingInline: theme.spacing(1.75),
    justifyContent: "center",
    minHeight: 42,
    whiteSpace: "nowrap"
  },
  periodFilters: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    marginTop: theme.spacing(2.5)
  },
  periodButton: {
    borderRadius: 999,
    fontWeight: 700,
    minWidth: 0,
    paddingInline: theme.spacing(2)
  },
  spotlightGrid: {
    marginTop: theme.spacing(2.5)
  },
  spotlightCard: {
    padding: theme.spacing(2),
    borderRadius: 22,
    minHeight: 148,
    background: isDark
      ? "linear-gradient(180deg, rgba(15, 23, 42, 0.95) 0%, rgba(30, 41, 59, 0.92) 100%)"
      : "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(245,250,248,0.98) 100%)",
    border: isDark ? "1px solid rgba(45, 212, 191, 0.16)" : "1px solid rgba(18, 140, 126, 0.10)",
    boxShadow: isDark ? "0 14px 34px rgba(2, 6, 23, 0.30)" : "0 14px 34px rgba(15, 23, 42, 0.06)"
  },
  spotlightHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(1),
    color: isDark ? "#5eead4" : "#0b6b60"
  },
  spotlightTitle: {
    color: isDark ? "#5eead4" : "#0b6b60",
    fontWeight: 800,
    fontSize: "0.82rem",
    textTransform: "uppercase",
    letterSpacing: "0.04em"
  },
  spotlightValue: {
    marginTop: theme.spacing(1.25),
    fontWeight: 800,
    color: theme.palette.text.primary,
    lineHeight: 1.2
  },
  spotlightSubtitle: {
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary,
    lineHeight: 1.5
  },
  panel: {
    height: "100%",
    padding: theme.spacing(2.5),
    borderRadius: 24,
    border: isDark ? "1px solid rgba(45, 212, 191, 0.16)" : "1px solid rgba(18, 140, 126, 0.10)",
    boxShadow: isDark ? "0 18px 40px rgba(2, 6, 23, 0.32)" : "0 18px 40px rgba(15, 23, 42, 0.08)",
    background: isDark
      ? "linear-gradient(180deg, rgba(15, 23, 42, 0.95) 0%, rgba(17, 24, 39, 0.94) 100%)"
      : "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.98) 100%)"
  },
  panelHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column"
    }
  },
  panelTitle: {
    color: isDark ? "#5eead4" : "#0b6b60",
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    fontSize: "0.82rem"
  },
  panelSubtitle: {
    marginTop: theme.spacing(0.75),
    color: theme.palette.text.secondary,
    lineHeight: 1.5
  },
  lineFilters: {
    minWidth: 190,
    "& .MuiOutlinedInput-root": {
      borderRadius: 14
    }
  },
  emptyState: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(4)
  },
  topContactsList: {
    display: "grid",
    gap: theme.spacing(1)
  },
  topContactsItem: {
    display: "grid",
    gridTemplateColumns: "auto 1fr auto",
    gap: theme.spacing(1.25),
    alignItems: "center",
    padding: theme.spacing(1.25, 1.5),
    borderRadius: 16,
    backgroundColor: isDark ? "rgba(45, 212, 191, 0.08)" : "rgba(18, 140, 126, 0.05)"
  },
  rankBadge: {
    width: 30,
    height: 30,
    borderRadius: 999,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: isDark ? "rgba(45, 212, 191, 0.18)" : "rgba(18, 140, 126, 0.12)",
    color: isDark ? "#99f6e4" : "#0b6b60",
    fontWeight: 800,
    fontSize: "0.8rem"
  },
  topContactsName: {
    fontWeight: 700,
    color: theme.palette.text.primary
  },
  topContactsMeta: {
    color: theme.palette.text.secondary,
    fontSize: "0.88rem",
    marginTop: theme.spacing(0.25)
  },
  topContactsValue: {
    fontWeight: 800,
    color: theme.palette.text.primary
  },
  statList: {
    display: "grid",
    gap: theme.spacing(1.25)
  },
  statItem: {
    padding: theme.spacing(1.25, 1.5),
    borderRadius: 16,
    backgroundColor: isDark ? "rgba(45, 212, 191, 0.09)" : "rgba(18, 140, 126, 0.06)"
  },
  statLabel: {
    color: theme.palette.text.secondary,
    fontSize: "0.84rem"
  },
  statValue: {
    marginTop: theme.spacing(0.35),
    fontWeight: 800,
    color: theme.palette.text.primary
  }
  });
});

const defaultSummary = {
  metrics: {
    usersCount: 0,
    queuesCount: 0,
    connectionsCount: 0,
    contactsCount: 0,
    openTicketsCount: 0,
    pendingTicketsCount: 0,
    closedTicketsTodayCount: 0,
    unreadTicketsCount: 0,
    groupTicketsCount: 0,
    privateTicketsCount: 0,
    internalTicketsCount: 0,
    internalTicketsDueSoonCount: 0,
    internalTicketsOverdueCount: 0
  },
  scope: {
    kind: "company",
    userId: null
  },
  workloadDistributions: {
    byUser: [],
    byQueue: [],
    byWhatsapp: []
  },
  topContacts: [],
  topContactsPeriodStart: null,
  analyticsPeriod: {
    key: "today",
    label: "Hoje",
    startDate: null,
    endDate: null,
    timeZone: "America/Sao_Paulo",
    dateBasis: "closedAt",
    dateBasisLabel: "Data de fechamento"
  },
  availableQueues: [],
  attendantTimeline: {
    queueId: null,
    bucket: "hour",
    points: [],
    series: []
  },
  commercialAlert: null
};

const escapeHtml = value =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const buildExcelTable = (title, rows) => {
  const body = rows
    .map(
      row =>
        `<tr>${row
          .map(cell => `<td>${escapeHtml(cell)}</td>`)
          .join("")}</tr>`
    )
    .join("");

  return `
    <table border="1" cellspacing="0" cellpadding="6">
      <tr><th colspan="${rows[0]?.length || 1}">${escapeHtml(title)}</th></tr>
      ${body}
    </table>
    <br />
  `;
};

const exportDashboardToExcel = (
  summary,
  companyName,
  selectedQueueName,
  selectedDateBasisLabel
) => {
  const metricRows = [
    ["Indicador", "Valor"],
    ["Em atendimento", summary.metrics.openTicketsCount],
    ["Aguardando", summary.metrics.pendingTicketsCount],
    ["Não lidos", summary.metrics.unreadTicketsCount],
    ["Encerrados hoje", summary.metrics.closedTicketsTodayCount],
    ["Usuários", summary.metrics.usersCount],
    ["Filas", summary.metrics.queuesCount],
    ["Conexões", summary.metrics.connectionsCount],
    ["Contatos", summary.metrics.contactsCount],
    ["Conversas privadas", summary.metrics.privateTicketsCount],
    ["Grupos", summary.metrics.groupTicketsCount],
    ["Tickets internos", summary.metrics.internalTicketsCount],
    ["Internos a vencer", summary.metrics.internalTicketsDueSoonCount],
    ["Internos vencidos", summary.metrics.internalTicketsOverdueCount]
  ];

  const byUserRows = [
    ["Atendente", "Atendimentos no período"],
    ...(summary.workloadDistributions.byUser || []).map(item => [item.label, item.value])
  ];
  const byQueueRows = [
    ["Fila", "Atendimentos no período"],
    ...(summary.workloadDistributions.byQueue || []).map(item => [item.label, item.value])
  ];
  const byWhatsappRows = [
    ["Conexão", "Atendimentos no período"],
    ...(summary.workloadDistributions.byWhatsapp || []).map(item => [item.label, item.value])
  ];
  const topContactsRows = [
    ["Cliente", "Número", "Atendimentos"],
    ...(summary.topContacts || []).map(item => [
      item.label,
      item.secondary || "-",
      item.value
    ])
  ];
  const timelineRows = [
    ["Período", ...(summary.attendantTimeline.series || [])],
    ...(summary.attendantTimeline.points || []).map(point => [
      point.label,
      ...(summary.attendantTimeline.series || []).map(series => point[series] || 0)
    ])
  ];

  const html = `
    <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
      <head><meta charset="utf-8" /></head>
      <body>
        <h2>${escapeHtml(companyName)} - Dashboard gerencial</h2>
        <p>Período: ${escapeHtml(summary.analyticsPeriod.label || "Hoje")}</p>
        <p>Base temporal: ${escapeHtml(selectedDateBasisLabel || "Data de fechamento")}</p>
        <p>Fila do gráfico: ${escapeHtml(selectedQueueName || "Todas")}</p>
        <p>Gerado em ${escapeHtml(new Date().toLocaleString("pt-BR"))}</p>
        ${buildExcelTable("Visão atual da operação", metricRows)}
        ${buildExcelTable("Atendimentos por atendente no período", byUserRows)}
        ${buildExcelTable("Atendimentos por fila no período", byQueueRows)}
        ${buildExcelTable("Atendimentos por conexão no período", byWhatsappRows)}
        ${buildExcelTable("Top clientes no período", topContactsRows)}
        ${buildExcelTable("Ritmo por atendente", timelineRows)}
      </body>
    </html>
  `;

  const blob = new Blob(["\ufeff", html], {
    type: "application/vnd.ms-excel;charset=utf-8;"
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `dashboard-gerencial-${new Date()
    .toISOString()
    .slice(0, 10)}.xls`;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};

const KpiCard = ({ classes, title, value, subtitle, icon }) => (
  <Paper className={classes.spotlightCard}>
    <div className={classes.spotlightHeader}>
      <Typography className={classes.spotlightTitle}>{title}</Typography>
      {icon}
    </div>
    <Typography variant="h4" className={classes.spotlightValue}>
      {value}
    </Typography>
    <Typography variant="body2" className={classes.spotlightSubtitle}>
      {subtitle}
    </Typography>
  </Paper>
);

const PiePanel = ({ classes, title, subtitle, data }) => {
  const theme = useTheme();
  const total = (data || []).reduce((sum, item) => sum + Number(item.value || 0), 0);

  return (
    <Paper className={classes.panel}>
      <Typography className={classes.panelTitle}>{title}</Typography>
      <Typography variant="body2" className={classes.panelSubtitle}>
        {subtitle}
      </Typography>
      {total > 0 ? (
        <ResponsiveContainer width="100%" height={280}>
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              nameKey="label"
              innerRadius={62}
              outerRadius={94}
              paddingAngle={3}
            >
              {data.map((item, index) => (
                <Cell
                  key={`${title}-${item.label}`}
                  fill={chartPalette[index % chartPalette.length]}
                />
              ))}
            </Pie>
            <Legend
              verticalAlign="bottom"
              formatter={value => (
                <span style={{ color: theme.palette.text.secondary }}>{value}</span>
              )}
            />
            <RechartsTooltip
              formatter={value => [`${value} atendimentos`, "Volume"]}
              labelFormatter={label => label}
            />
          </PieChart>
        </ResponsiveContainer>
      ) : (
        <Typography variant="body2" className={classes.emptyState}>
          Ainda não há volume suficiente no período para preencher este gráfico.
        </Typography>
      )}
    </Paper>
  );
};

const AttendantTimelinePanel = ({
  classes,
  timeline,
  availableQueues,
  selectedQueueId,
  onQueueChange,
  periodLabel,
  timeZone
}) => {
  const theme = useTheme();
  const points = Array.isArray(timeline?.points) ? timeline.points : [];
  const series = Array.isArray(timeline?.series) ? timeline.series : [];

  return (
    <Paper className={classes.panel}>
      <div className={classes.panelHeader}>
        <div>
          <Typography className={classes.panelTitle}>Ritmo da operação</Typography>
          <Typography variant="body2" className={classes.panelSubtitle}>
            Evolução dos atendimentos por atendente em {periodLabel.toLowerCase()}.
          </Typography>
        </div>
        <TextField
          select
          size="small"
          variant="outlined"
          label="Fila"
          value={selectedQueueId}
          onChange={event => onQueueChange(event.target.value)}
          className={classes.lineFilters}
        >
          <MenuItem value="">Todas</MenuItem>
          {availableQueues.map(queue => (
            <MenuItem key={queue.id} value={String(queue.id)}>
              {queue.name}
            </MenuItem>
          ))}
        </TextField>
      </div>
      {series.length > 0 ? (
        <ResponsiveContainer width="100%" height={340}>
          <LineChart
            data={points}
            margin={{ top: 12, right: 24, left: 0, bottom: 8 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.type === "dark" ? "rgba(148, 163, 184, 0.30)" : "rgba(148, 163, 184, 0.22)"} />
            <XAxis dataKey="label" stroke={theme.palette.text.secondary} />
            <YAxis allowDecimals={false} stroke={theme.palette.text.secondary} />
            <RechartsTooltip />
            <Legend />
            {series.map((seriesName, index) => (
              <Line
                key={seriesName}
                type={timeline?.bucket === "hour" ? "linear" : "monotone"}
                dataKey={seriesName}
                stroke={chartPalette[index % chartPalette.length]}
                strokeWidth={timeline?.bucket === "hour" ? 3 : 2.5}
                dot={{ r: timeline?.bucket === "hour" ? 4 : 2 }}
                activeDot={{ r: timeline?.bucket === "hour" ? 6 : 5 }}
                connectNulls
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      ) : (
        <Typography variant="body2" className={classes.emptyState}>
          Ainda não há atendimentos suficientes nesse recorte para desenhar a linha por atendente.
        </Typography>
      )}
    </Paper>
  );
};

const TopContactsPanel = ({ classes, data, periodLabel }) => (
  <Paper className={classes.panel}>
    <Typography className={classes.panelTitle}>Top 10 clientes</Typography>
    <Typography variant="body2" className={classes.panelSubtitle}>
      Clientes com maior volume de atendimentos em {periodLabel.toLowerCase()}.
    </Typography>
    {data.length > 0 ? (
      <div className={classes.topContactsList}>
        {data.map((item, index) => (
          <div className={classes.topContactsItem} key={`top-contact-${item.id || item.label}-${index}`}>
            <div className={classes.rankBadge}>{index + 1}</div>
            <div>
              <div className={classes.topContactsName}>{item.label}</div>
              <div className={classes.topContactsMeta}>{item.secondary || "Sem número"}</div>
            </div>
            <div className={classes.topContactsValue}>{item.value}</div>
          </div>
        ))}
      </div>
    ) : (
      <Typography variant="body2" className={classes.emptyState}>
        Ainda não há clientes suficientes no período para montar este ranking.
      </Typography>
    )}
  </Paper>
);

const Dashboard = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const companyName = user?.company?.name || "DeskAdvisor";
  const isUserScoped = user?.profile === "user";
  const [summary, setSummary] = useState(defaultSummary);
  const [loading, setLoading] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState("today");
  const [selectedDateBasis, setSelectedDateBasis] = useState("closedAt");
  const [selectedTimelineQueueId, setSelectedTimelineQueueId] = useState("");
  const latestSummaryRequestId = useRef(0);

  const fetchSummary = useCallback(async () => {
    const requestId = latestSummaryRequestId.current + 1;
    latestSummaryRequestId.current = requestId;

    try {
      setLoading(true);
      const { data } = await api.get("/dashboard/summary", {
        params: {
          period: selectedPeriod,
          dateBasis: selectedDateBasis,
          timelineQueueId: selectedTimelineQueueId || undefined
        }
      });

      if (latestSummaryRequestId.current !== requestId) {
        return;
      }

      setSummary({
        ...defaultSummary,
        ...data,
        workloadDistributions: {
          ...defaultSummary.workloadDistributions,
          ...(data?.workloadDistributions || {})
        },
        attendantTimeline: {
          ...defaultSummary.attendantTimeline,
          ...(data?.attendantTimeline || {})
        }
      });
    } catch (err) {
      if (latestSummaryRequestId.current !== requestId) {
        return;
      }
      toastError(err);
    } finally {
      if (latestSummaryRequestId.current === requestId) {
        setLoading(false);
      }
    }
  }, [selectedDateBasis, selectedPeriod, selectedTimelineQueueId]);

  React.useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const managementHighlights = useMemo(() => {
    const topUser = summary.workloadDistributions.byUser?.[0];
    const topQueue = summary.workloadDistributions.byQueue?.[0];
    const topWhatsapp = summary.workloadDistributions.byWhatsapp?.[0];

    return [
      {
        title: "Atendente em destaque",
        value: topUser?.label || "Sem distribuição",
        subtitle: topUser
          ? `${topUser.value} atendimentos concentrados no período.`
          : "Assim que houver movimentação, o ranking aparece aqui."
      },
      {
        title: "Fila mais pressionada",
        value: topQueue?.label || "Sem distribuição",
        subtitle: topQueue
          ? `${topQueue.value} atendimentos concentrados nesta fila.`
          : "Nenhuma fila com volume suficiente neste recorte."
      },
      {
        title: "Conexão mais usada",
        value: topWhatsapp?.label || "Sem distribuição",
        subtitle: topWhatsapp
          ? `${topWhatsapp.value} atendimentos usando esta conexão.`
          : "Nenhuma conexão em destaque no período."
      }
    ];
  }, [summary.workloadDistributions]);

  const selectedQueueName = useMemo(() => {
    if (!selectedTimelineQueueId) {
      return "Todas";
    }

    return (
      summary.availableQueues.find(
        queue => String(queue.id) === String(selectedTimelineQueueId)
      )?.name || "Todas"
    );
  }, [selectedTimelineQueueId, summary.availableQueues]);

  const selectedDateBasisLabel = useMemo(
    () =>
      dateBasisOptions.find(option => option.value === selectedDateBasis)?.label ||
      "Data de fechamento",
    [selectedDateBasis]
  );

  const periodDescriptor = useMemo(() => {
    switch (selectedPeriod) {
      case "7d":
        return "nos últimos 7 dias";
      case "30d":
        return "nos últimos 30 dias";
      case "month":
        return "no mês atual";
      case "today":
      default:
        return "hoje";
    }
  }, [selectedPeriod]);

  const closedTicketsTitle =
    selectedPeriod === "today" ? "Encerrados hoje" : "Encerrados no período";

  return (
    <Container maxWidth="lg" className={classes.container}>
      <Paper className={classes.heroCard}>
        <div className={classes.heroHeader}>
          <div className={classes.heroIdentity}>
            <Typography className={classes.heroEyebrow}>
              Dashboard gerencial
            </Typography>
            <Typography variant="h4" className={classes.heroTitle}>
              {companyName}
            </Typography>
            <div className={classes.periodFilters}>
              {periodOptions.map(option => (
                <Button
                  key={option.value}
                  variant={selectedPeriod === option.value ? "contained" : "outlined"}
                  color="primary"
                  onClick={() => {
                    setSelectedTimelineQueueId("");
                    setSelectedPeriod(option.value);
                  }}
                  className={classes.periodButton}
                >
                  {option.label}
                </Button>
              ))}
              <TextField
                select
                size="small"
                variant="outlined"
                label="Base"
                value={selectedDateBasis}
                onChange={event => {
                  setSelectedTimelineQueueId("");
                  setSelectedDateBasis(event.target.value);
                }}
                className={classes.lineFilters}
              >
                {dateBasisOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </div>
          </div>
          <div className={classes.heroNoticeSlot}>
            {summary.commercialAlert ? (
              <Paper
                elevation={0}
                className={`${classes.commercialAlert} ${
                  summary.commercialAlert.kind === "danger"
                    ? classes.commercialAlertDanger
                    : ""
                }`}
              >
                <Typography
                  className={`${classes.commercialAlertTitle} ${
                    summary.commercialAlert.kind === "danger"
                      ? classes.commercialAlertTitleDanger
                      : ""
                  }`}
                >
                  {summary.commercialAlert.title}
                </Typography>
                <Typography
                  variant="body2"
                  className={`${classes.commercialAlertText} ${
                    summary.commercialAlert.kind === "danger"
                      ? classes.commercialAlertTextDanger
                      : ""
                  }`}
                >
                  {summary.commercialAlert.description}
                </Typography>
              </Paper>
            ) : null}
          </div>
          <div className={classes.heroActionsPanel}>
            <Typography className={classes.heroActionsLabel}>
              Ações rápidas
            </Typography>
            <div className={classes.heroActions}>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<RefreshIcon />}
                onClick={fetchSummary}
                disabled={loading}
                className={classes.actionButton}
              >
                Atualizar
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<GetAppIcon />}
                onClick={() =>
                  exportDashboardToExcel(
                    summary,
                    companyName,
                    selectedQueueName,
                    selectedDateBasisLabel
                  )
                }
                className={classes.actionButton}
              >
                Exportar Excel
              </Button>
            </div>
          </div>
        </div>
        <Grid container spacing={2} className={classes.spotlightGrid}>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard
              classes={classes}
              title="Em atendimento"
              value={summary.metrics.openTicketsCount}
              subtitle="Visão atual dos tickets em execução."
              icon={<AssessmentOutlinedIcon />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard
              classes={classes}
              title="Aguardando"
              value={summary.metrics.pendingTicketsCount}
              subtitle="Fila atual de tickets aguardando ação."
              icon={<AccessTimeIcon />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard
              classes={classes}
              title="Não lidos"
              value={summary.metrics.unreadTicketsCount}
              subtitle="Conversas com mensagens pendentes agora."
              icon={<PeopleOutlineIcon />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard
              classes={classes}
              title={closedTicketsTitle}
              value={summary.metrics.closedTicketsTodayCount}
              subtitle="Fechamentos registrados no dia atual."
              icon={<TrendingUpIcon />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <KpiCard
              classes={classes}
              title="Abertos"
              value={summary.metrics.internalTicketsCount}
              subtitle={
                isUserScoped
                  ? "Tickets internos ativos atribuidos a voce."
                  : "Tickets internos ativos da operacao."
              }
              icon={<AssignmentOutlinedIcon />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <KpiCard
              classes={classes}
              title="A vencer"
              value={summary.metrics.internalTicketsDueSoonCount}
              subtitle="Itens internos que vencem nas proximas 24 horas."
              icon={<AccessTimeIcon />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <KpiCard
              classes={classes}
              title="Vencidos"
              value={summary.metrics.internalTicketsOverdueCount}
              subtitle="Itens internos fora do prazo neste momento."
              icon={<ErrorOutlineOutlinedIcon />}
            />
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={3}>
        {!isUserScoped &&
          managementHighlights.map(item => (
          <Grid item xs={12} md={4} key={item.title}>
            <Paper className={classes.panel}>
              <Typography className={classes.panelTitle}>{item.title}</Typography>
              <Typography variant="h5" className={classes.spotlightValue}>
                {item.value}
              </Typography>
              <Typography variant="body2" className={classes.spotlightSubtitle}>
                {item.subtitle}
              </Typography>
            </Paper>
          </Grid>
          ))}

        <Grid item xs={12} md={4}>
          <PiePanel
            classes={classes}
            title="Atendimentos por atendente"
            subtitle={`Distribuição dos atendimentos em ${summary.analyticsPeriod.label.toLowerCase()}.`}
            data={summary.workloadDistributions.byUser || []}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <PiePanel
            classes={classes}
            title="Atendimentos por fila"
            subtitle={`Onde a demanda ficou concentrada em ${summary.analyticsPeriod.label.toLowerCase()}.`}
            data={summary.workloadDistributions.byQueue || []}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <PiePanel
            classes={classes}
            title="Atendimentos por conexão"
            subtitle={`Carga operacional por conexão em ${summary.analyticsPeriod.label.toLowerCase()}.`}
            data={summary.workloadDistributions.byWhatsapp || []}
          />
        </Grid>

        <Grid item xs={12} md={8}>
          <AttendantTimelinePanel
            classes={classes}
            timeline={summary.attendantTimeline}
            availableQueues={summary.availableQueues}
            selectedQueueId={selectedTimelineQueueId}
            onQueueChange={setSelectedTimelineQueueId}
            periodLabel={summary.analyticsPeriod.label}
            timeZone={summary.analyticsPeriod.timeZone || "America/Sao_Paulo"}
          />
        </Grid>
        {!isUserScoped && <Grid item xs={12} md={4}>
          <Paper className={classes.panel}>
            <Typography className={classes.panelTitle}>Base operacional</Typography>
            <Typography variant="body2" className={classes.panelSubtitle}>
              Visão resumida da estrutura e do mix atual de atendimento.
            </Typography>
            <div className={classes.statList}>
              <div className={classes.statItem}>
                <div className={classes.statLabel}>Usuários ativos</div>
                <div className={classes.statValue}>{summary.metrics.usersCount}</div>
              </div>
              <div className={classes.statItem}>
                <div className={classes.statLabel}>Filas configuradas</div>
                <div className={classes.statValue}>{summary.metrics.queuesCount}</div>
              </div>
              <div className={classes.statItem}>
                <div className={classes.statLabel}>Conexões WhatsApp</div>
                <div className={classes.statValue}>{summary.metrics.connectionsCount}</div>
              </div>
              <div className={classes.statItem}>
                <div className={classes.statLabel}>Contatos cadastrados</div>
                <div className={classes.statValue}>{summary.metrics.contactsCount}</div>
              </div>
              <div className={classes.statItem}>
                <div className={classes.statLabel}>Privados / grupos</div>
                <div className={classes.statValue}>
                  {summary.metrics.privateTicketsCount} / {summary.metrics.groupTicketsCount}
                </div>
              </div>
            </div>
          </Paper>
        </Grid>}

        <Grid item xs={12}>
          <TopContactsPanel
            classes={classes}
            data={summary.topContacts || []}
            periodLabel={summary.analyticsPeriod.label}
          />
        </Grid>
      </Grid>
    </Container>
  );
};

export default Dashboard;
